import apiClient from './client';
import { Bulletin, Artikel } from '@/types';

export interface BulletinListParams {
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface BulletinListResponse {
  data: Bulletin[];
  total: number;
  page: number;
  limit: number;
}

export interface MessageResponse {
  message: string;
  id?: number;
  cover?: string | null;
  url?: string;
  path?: string;
}

export interface UploadBrowserDirectory {
  name: string;
  dir: string;
}

export interface UploadBrowserFile {
  name: string;
  path: string;
  url: string;
  size: number;
  modifiedAt: number;
}

export interface UploadBrowserResponse {
  currentDir: string;
  parentDir: string;
  directories: UploadBrowserDirectory[];
  files: UploadBrowserFile[];
}

export const bulletinApi = {
  getAll: async (params: BulletinListParams = {}): Promise<BulletinListResponse> => {
    const response = await apiClient.get('/api/bulletin', { params });
    return response.data;
  },

  getById: async (id: number): Promise<Bulletin> => {
    const response = await apiClient.get(`/api/bulletin/${id}`);
    return response.data;
  },

  create: async (data: Partial<Bulletin>): Promise<MessageResponse> => {
    const response = await apiClient.post('/api/bulletin', data);
    return response.data;
  },

  update: async (id: number, data: Partial<Bulletin>): Promise<MessageResponse> => {
    const response = await apiClient.put(`/api/bulletin/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<MessageResponse> => {
    const response = await apiClient.delete(`/api/bulletin/${id}`);
    return response.data;
  },

  uploadCover: async (id: number, file: File): Promise<MessageResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/api/bulletin/${id}/cover/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  setCoverLink: async (id: number, url: string): Promise<MessageResponse> => {
    const response = await apiClient.post(`/api/bulletin/${id}/cover/link`, { url });
    return response.data;
  },

  uploadInlineImage: async (file: File): Promise<MessageResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post('/api/bulletin/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  browseUploads: async (dir = ''): Promise<UploadBrowserResponse> => {
    const response = await apiClient.get('/api/bulletin/uploads/browse', {
      params: { dir },
    });
    return response.data;
  },

  copyUploadImage: async (path: string, target: 'cover' | 'richtext/bulletin' | 'richtext/news'): Promise<MessageResponse> => {
    const response = await apiClient.post('/api/bulletin/uploads/copy-image', {
      path,
      target,
    });
    return response.data;
  },

  getArtikels: async (bulletinId: number): Promise<Artikel[]> => {
    const response = await apiClient.get(`/api/bulletin/${bulletinId}/artikels`);
    return response.data;
  },
};
